import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaImage, FaChevronLeft, FaChevronRight, FaShoppingCart, FaBox, FaTag, FaDollarSign } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyBranchRequestDetailsNew = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImageIndex, setModalImageIndex] = useState(0);

    useEffect(() => {
        setTitle('تفاصيل طلب الفرع');
        if (id) {
            fetchRequestDetails();
        }
    }, [id, setTitle]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchRequestDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();

            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                navigate('/pharmacy-branch-requests-new');
                return;
            }

            // التحقق من أن المستخدم هو pharmacy_branch
            if (userRole !== 'pharmacy_branch') {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة', { containerId: 'main-toast-container' });
                navigate('/');
                return;
            }

            const response = await axios.get(`${BASE_URL}/pharmacy/branches/requests/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                setRequest(response.data.data);
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل الطلب');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب');
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب', { containerId: 'main-toast-container' });
        } finally {
            setLoading(false);
        }
    };


    const nextImage = () => {
        if (request && request.images) {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === request.images.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    const prevImage = () => {
        if (request && request.images) {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === 0 ? request.images.length - 1 : prevIndex - 1
            );
        }
    };

    const openImageModal = (index) => {
        setModalImageIndex(index);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
    };

    const nextModalImage = () => {
        if (request && request.images) {
            setModalImageIndex((prevIndex) =>
                prevIndex === request.images.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    const prevModalImage = () => {
        if (request && request.images) {
            setModalImageIndex((prevIndex) =>
                prevIndex === 0 ? request.images.length - 1 : prevIndex - 1
            );
        }
    };


    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>خطأ</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-branch-requests-new')}
                        >
                            العودة لطلبات الفروع
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!request) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>الطلب غير موجود</h4>
                        <p>الطلب المطلوب غير موجود أو تم حذفه</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-branch-requests-new')}
                        >
                            العودة لطلبات الفروع
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-branch-requests-new')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <div></div>
                </div>

                {/* Request Details Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="card shadow-sm mb-4"
                    style={{ borderRadius: '12px' }}>

                    <div className="card-body p-4">
                        {/* Request Images Slider */}
                        {request.images && request.images.length > 0 && (
                            <div className="mb-4">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaImage className="mx-2" style={{ color: '#184194' }} />
                                    صور الطلب
                                </h5>
                                <div className="position-relative" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    <div style={{
                                        position: 'relative',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <img
                                            src={request.images[currentImageIndex]}
                                            alt={`Request ${currentImageIndex + 1}`}
                                            className="img-fluid"
                                            style={{
                                                width: '100%',
                                                height: '300px',
                                                objectFit: 'cover',
                                                display: 'block',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => openImageModal(currentImageIndex)}
                                        />

                                        {/* Navigation Buttons */}
                                        {request.images.length > 1 && (
                                            <>
                                                <button
                                                    onClick={prevImage}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        left: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '40px',
                                                        height: '40px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronLeft style={{ color: '#184194' }} />
                                                </button>
                                                <button
                                                    onClick={nextImage}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        right: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '40px',
                                                        height: '40px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronRight style={{ color: '#184194' }} />
                                                </button>
                                            </>
                                        )}

                                        {/* Image Counter */}
                                        {request.images.length > 1 && (
                                            <div className="position-absolute" style={{
                                                bottom: '10px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '14px'
                                            }}>
                                                {currentImageIndex + 1} / {request.images.length}
                                            </div>
                                        )}
                                    </div>

                                    {/* Thumbnails */}
                                    {request.images.length > 1 && (
                                        <div className="d-flex justify-content-center gap-2 mt-3">
                                            {request.images.map((image, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => {
                                                        setCurrentImageIndex(index);
                                                        openImageModal(index);
                                                    }}
                                                    className={`btn p-0 ${index === currentImageIndex ? 'border-primary' : ''}`}
                                                    style={{
                                                        border: '2px solid transparent',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={image}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            objectFit: 'cover',
                                                            display: 'block'
                                                        }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Request Information */}
                        <div className="row">
                            <div className="col-12 mb-3">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaShoppingCart className="mx-2" style={{ color: '#184194' }} />
                                    تفاصيل الطلب
                                </h5>
                                <div className="row">
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">اسم العميل</h6>
                                        <p className="fw-medium">{request.client?.name || (typeof request.client === 'string' ? request.client : 'غير محدد')}</p>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">رقم الهاتف</h6>
                                        <p className="fw-medium">{request.client?.phone || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">الايميل</h6>
                                        <p className="fw-medium">{request.client?.email || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">الحالة</h6>
                                        <p className="fw-medium">
                                            <span className={`badge ${request.status === 'pending' ? 'bg-warning text-dark' :
                                                request.status === 'doctor_responded' ? 'bg-info text-white' :
                                                    request.status === 'completed' ? 'bg-success text-white' :
                                                        'bg-danger text-white'
                                                }`}>
                                                {request.status === 'pending' ? 'في الانتظار' :
                                                    request.status === 'doctor_responded' ? 'رد الصيدلية' :
                                                        request.status === 'completed' ? 'مكتمل' : 'ملغي'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">تاريخ الطلب</h6>
                                        <p className="fw-medium">{new Date(request.created_at).toLocaleDateString('ar-SA', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            calendar: 'gregory'
                                        })}</p>
                                    </div>
                                </div>
                                {request.note && (
                                    <div className="col-12 mb-3">
                                        <h6 className="text-muted">ملاحظة العميل</h6>
                                        <p className="fw-medium" style={{
                                            backgroundColor: '#f8f9fa',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e9ecef'
                                        }}>
                                            {request.note}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Response Items */}
                        {request.items && request.items.length > 0 ? (
                            <div className="mt-4">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaBox className="mx-2" style={{ color: '#184194' }} />
                                    المنتجات المضافة في الرد
                                </h5>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th className="border-0 py-3 px-3 fw-semibold">اسم المنتج</th>
                                                <th className="border-0 py-3 px-3 fw-semibold">اسم الوحدة</th>
                                                <th className="border-0 py-3 px-3 fw-semibold text-center">سعر الوحدة</th>
                                                <th className="border-0 py-3 px-3 fw-semibold">الملاحظات</th>
                                                <th className="border-0 py-3 px-3 fw-semibold text-center">الكمية</th>
                                                <th className="border-0 py-3 px-3 fw-semibold text-center">السعر النهائي</th>
                                                <th className="border-0 py-3 px-3 fw-semibold text-center">المجموع</th>
                                                <th className="border-0 py-3 px-3 fw-semibold text-center">الحالة</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {request.items.map((item, index) => (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                                    className="border-bottom"
                                                >
                                                    <td className="py-3 px-3">
                                                        <div className="d-flex align-items-center">
                                                            <div>
                                                                <div className="fw-medium">{item.product_name}</div>
                                                                <small className="text-muted">ID: {item.product_id}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        {item.unit_name ? (
                                                            <span className="badge bg-light text-dark">
                                                                {item.unit_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">غير محدد</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        <span className="fw-medium">
                                                            {item.unit_price || 0} ج.م
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        {item.note ? (
                                                            <span className="text-muted">{item.note}</span>
                                                        ) : (
                                                            <span className="text-muted">لا توجد ملاحظات</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        <span className="fw-medium">{item.quantity}</span>
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        <span className="fw-medium d-flex align-items-center justify-content-center">
                                                            <FaDollarSign className="mx-1" style={{ fontSize: '12px', color: '#28a745' }} />
                                                            {item.final_price || 0} ج.م
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        <span className="fw-medium" style={{ color: '#28a745' }}>
                                                            {item.line_total || 0} ج.م
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3 text-center">
                                                        {item.status ? (
                                                            <span className="badge bg-success text-white">متاح</span>
                                                        ) : item.status === false ? (
                                                            <span className="badge bg-danger text-white">غير متاح</span>
                                                        ) : (
                                                            <span className="badge bg-secondary text-white">غير محدد</span>
                                                        )}
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Total Summary */}
                                <div className="row mt-3">
                                    <div className="col-md-6"></div>
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="fw-bold">المجموع الكلي:</span>
                                                    <span className="fw-bold fs-5" style={{ color: '#28a745' }}>
                                                        {request.items.reduce((total, item) => total + (item.line_total || 0), 0)} ج.م
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4">
                                <div className="alert alert-info text-center">
                                    <h6 className="mb-2">لا توجد منتجات مضافة في الرد</h6>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Image Modal */}
                {showImageModal && request.images && (
                    <div
                        className="modal fade show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}
                        onClick={closeImageModal}
                    >
                        <div className="modal-dialog modal-xl modal-dialog-centered">
                            <div className="modal-content" style={{ backgroundColor: 'transparent', border: 'none' }}>
                                <div className="modal-header border-0 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={closeImageModal}
                                        style={{ fontSize: '1.5rem' }}
                                    ></button>
                                </div>
                                <div className="modal-body text-center">
                                    <div className="position-relative d-inline-block">
                                        <img
                                            src={request.images[modalImageIndex]}
                                            alt={`Request Image ${modalImageIndex + 1}`}
                                            className="img-fluid"
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '90vw',
                                                borderRadius: '12px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                            }}
                                        />

                                        {/* Navigation Buttons */}
                                        {request.images.length > 1 && (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        prevModalImage();
                                                    }}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        left: '-60px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronLeft style={{ color: '#184194', fontSize: '18px' }} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        nextModalImage();
                                                    }}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        right: '-60px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronRight style={{ color: '#184194', fontSize: '18px' }} />
                                                </button>
                                            </>
                                        )}

                                        {/* Image Counter */}
                                        {request.images.length > 1 && (
                                            <div className="position-absolute" style={{
                                                bottom: '-50px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '8px 16px',
                                                borderRadius: '25px',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}>
                                                {modalImageIndex + 1} / {request.images.length}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PharmacyBranchRequestDetailsNew;
